/**
 * `lazyWithRetry` — drop-in remplaçant de `React.lazy` qui réessaie un import
 * dynamique échoué au lieu de remonter directement une erreur de chunk.
 *
 * Pourquoi : les chunks JS sont servis en `Cache-Control: immutable`. Si la
 * copie locale d'un chunk est cassée (téléchargement interrompu, course
 * modulepreload, blip réseau), le navigateur la re-sert sans jamais
 * revalider — et `window.location.reload()` n'y change rien (le reload
 * réutilise la copie `immutable`). Résultat : « Failed to fetch dynamically
 * imported module » en boucle, sans échappatoire.
 *
 * Stratégie : avant chaque retry, on re-fetch le chunk avec `cache: 'reload'`,
 * ce qui force le navigateur à re-télécharger depuis le réseau et à écraser
 * l'entrée de cache HTTP. Le `import()` suivant lit alors la copie saine.
 *
 * Dernier recours (chunk réellement disparu = hash mort post-deploy) : le
 * retry échoue, l'erreur remonte à l'ErrorBoundary qui recharge la page.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { isChunkError } from './chunkErrorReload';

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 400;

type ModuleDefault<T> = { default: T };

/**
 * Extrait l'URL du chunk depuis un message d'erreur d'import dynamique.
 * Chrome inclut l'URL ("Failed to fetch dynamically imported module: https://…/x.js").
 * Renvoie `null` si aucune URL `.js`/`.mjs` n'est trouvée (ex : message terse
 * de Safari "Importing a module script failed.").
 */
export function extractChunkUrl(message: string | null | undefined): string | null {
  if (!message) return null;
  const match = message.match(/https?:\/\/\S+?\.m?js/i);
  return match ? match[0] : null;
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export interface WithRetryOptions {
  /** Nombre de retries APRÈS la tentative initiale (défaut : 2 → 3 essais). */
  retries?: number;
  /** Délai de base entre tentatives, multiplié par le numéro de tentative. */
  delayMs?: number;
  /** Filtre : ne réessaie que si vrai. Défaut : toujours. */
  shouldRetry?: (err: unknown) => boolean;
  /** Hook exécuté avant chaque retry (numéro de tentative 1-indexé). */
  onBeforeRetry?: (attempt: number, err: unknown) => void | Promise<void>;
  /** Injection du sleep — permet des tests instantanés. */
  sleepFn?: (ms: number) => Promise<void>;
}

/**
 * Retry générique avec backoff linéaire. Pur et injectable → testable hors
 * navigateur.
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  opts: WithRetryOptions = {},
): Promise<T> {
  const retries = opts.retries ?? MAX_RETRIES;
  const delayMs = opts.delayMs ?? RETRY_DELAY_MS;
  const shouldRetry = opts.shouldRetry ?? (() => true);
  const sleepFn = opts.sleepFn ?? defaultSleep;

  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (attempt >= retries || !shouldRetry(err)) throw err;
      await opts.onBeforeRetry?.(attempt + 1, err);
      await sleepFn(delayMs * (attempt + 1));
    }
  }
}

/**
 * Force le navigateur à re-télécharger un chunk depuis le réseau en écrasant
 * l'entrée de cache HTTP — ce que le cache `immutable` interdit autrement.
 */
async function refreshChunkCache(url: string): Promise<void> {
  try {
    await fetch(url, { cache: 'reload', credentials: 'same-origin' });
  } catch {
    // Réseau indisponible : la tentative d'import suivante échouera et sera
    // soit re-retryée, soit remontée à l'ErrorBoundary (reload de la page).
  }
}

/**
 * Drop-in remplaçant de `React.lazy`. Même signature, mais réessaie l'import
 * dynamique (avec refresh du cache HTTP) sur erreur de chunk avant d'abandonner.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<ModuleDefault<T>>,
): LazyExoticComponent<T> {
  return lazy(() =>
    withRetry(importer, {
      shouldRetry: (err) => isChunkError((err as Error | undefined)?.message),
      onBeforeRetry: async (attempt, err) => {
        const url = extractChunkUrl((err as Error | undefined)?.message);
        console.warn(
          `[lazyWithRetry] import dynamique échoué — retry ${attempt}/${MAX_RETRIES}`,
          url ?? (err as Error | undefined)?.message ?? err,
        );
        if (url) await refreshChunkCache(url);
      },
    }),
  );
}
